import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../store';

// CLI のオプション一覧と説明文。? マークで hover 表示する
const OPTION_DESCRIPTIONS = {
  url: 'クロール対象の URL。https:// から始まる完全な形で入力してください。',
  depth:
    'クロールを「何階層」まで追うかの上限。例えば 2 にすると `https://example.com/about/team/` までは取りますが、その下のリンクは追いません。既定は 5。',
  maxPages:
    '取得するページ数の上限。これを超えるとクロールを打ち切り、未取得 URL は出力 JSON の `excluded` に記録されます。既定は 5000。',
  includeSubdomain:
    'サブドメイン（blog.example.com など）も同じサイトとして辿ります。既定では含めません。',
  output:
    '出力先の JSON ファイルパス。指定しない場合はデスクトップに `sitemap-{ドメイン}-{日時}.json` で保存されます。',
  force: '出力先に同名ファイルがあっても確認なしで上書きします。CI 用途や繰り返し実行時に便利。',
  slim:
    '内部リンクの詳細と画像情報を出力から省略します。容量が**半分以下**になるので、大規模サイトを AI に渡す前に絞り込みたいときに便利。',
  noInternalLinks:
    'ページごとの内部リンク詳細（リンク URL とアンカーテキスト）を省略します。--slim の片方だけ指定したいとき向け。',
  noImages: '画像情報（src / alt / 寸法）を省略します。--slim の片方だけ指定したいとき向け。',
  concurrency:
    '同時に投げるリクエストの数。サーバーに優しくしたい場合は 1〜2、急ぎたい場合は 5〜10 程度。既定は 3。',
  rateLimit:
    '1 秒あたりの最大リクエスト数。429 (Too Many Requests) を検出したら自動で半減します。既定は 5。',
  timeout:
    '1 リクエストあたりのタイムアウト（ミリ秒）。重いサイトでは長め、急ぎたいときは短めに。既定は 10000ms。',
  userAgent: 'クロール時に名乗る User-Agent。サーバー側の判定や統計でこのツールを識別したい場合に変更。',
  js: 'JavaScript 描画モード。auto は「テキストが少なければ自動で Playwright 起動」。on は強制 ON、off は強制 OFF。',
  noRespectRobots:
    'robots.txt を**無視**してクロールします。自社サイトの調査など正当な理由がある場合のみ使用してください。',
};

type JsMode = 'auto' | 'on' | 'off';

interface BuilderState {
  url: string;
  depth: string;
  maxPages: string;
  includeSubdomain: boolean;
  output: string;
  force: boolean;
  slim: boolean;
  noInternalLinks: boolean;
  noImages: boolean;
  concurrency: string;
  rateLimit: string;
  timeout: string;
  userAgent: string;
  js: JsMode;
  noRespectRobots: boolean;
}

const DEFAULT_STATE: BuilderState = {
  url: '',
  depth: '',
  maxPages: '',
  includeSubdomain: false,
  output: '',
  force: false,
  slim: false,
  noInternalLinks: false,
  noImages: false,
  concurrency: '',
  rateLimit: '',
  timeout: '',
  userAgent: '',
  js: 'auto',
  noRespectRobots: false,
};

export function CommandBuilder() {
  const closeCommandBuilder = useStore((s) => s.closeCommandBuilder);
  const [state, setState] = useState<BuilderState>(DEFAULT_STATE);
  const [copied, setCopied] = useState(false);

  // セクションの開閉状態
  const [openSection, setOpenSection] = useState<{
    crawl: boolean;
    output: boolean;
    behavior: boolean;
    js: boolean;
    advanced: boolean;
  }>({
    crawl: false,
    output: false,
    behavior: false,
    js: false,
    advanced: false,
  });

  const toggleSection = (key: keyof typeof openSection): void => {
    setOpenSection((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // リアルタイム生成コマンド
  const command = useMemo(() => buildCommand(state), [state]);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard 失敗は無視 */
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl space-y-3 px-6 py-4">
          <button
            onClick={closeCommandBuilder}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
          >
            ← 戻る
          </button>
          <h1 className="text-lg font-bold text-slate-900">使い方・ターミナル用のコマンド生成</h1>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-4 px-6 py-6">
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-5 text-sm leading-relaxed text-slate-800">
          <pre className="overflow-x-auto rounded-md bg-slate-900 px-3 py-2 font-mono text-xs text-slate-100">
            npx 4ge-sitemap &lt;URL&gt;
          </pre>

          <p>
            この画面は、対象サイトを巡回して URL・タイトル・見出し・画像 alt・JSON-LD などを 1 つの JSON にまとめる
            <strong className="text-slate-900">ターミナル用コマンド</strong>を組み立てる画面です。（
            <a
              href="https://www.npmjs.com/package/4ge-sitemap"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:text-blue-700 hover:underline"
            >
              詳しくはこちら
            </a>
            ）
          </p>

          <section>
            <h3 className="mb-1.5 font-mono text-sm font-semibold text-slate-900">&lt;手順&gt;</h3>
            <ol className="ml-5 list-decimal space-y-1.5">
              <li>
                <strong className="text-slate-900">URL を入力</strong>：下の入力欄に URL を入れると、コマンドが自動生成されます
              </li>
              <li>
                <strong className="text-slate-900">コピー</strong>：黒い箱の右上の「コピー」ボタンで丸ごとコピー
              </li>
              <li>
                <strong className="text-slate-900">実行</strong>：ターミナル（zsh / bash など）に貼り付けて実行。完了後、デスクトップに{' '}
                <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] text-slate-900">sitemap-&#123;ドメイン&#125;-&#123;日時&#125;.json</code>{' '}
                が保存されます
              </li>
            </ol>
            <p className="mt-2 text-xs text-slate-600">
              ※ オプションは未入力でOK。必要に応じて使い分けてください。
            </p>
          </section>

          <section>
            <h3 className="mb-1.5 font-mono text-sm font-semibold text-slate-900">&lt;データの使い方&gt;</h3>
            <ul className="ml-5 list-disc space-y-1.5">
              <li>
                <strong className="text-slate-900">AI で解析</strong>：ChatGPT や Claude にそのまま投げれば、正確なサイト構造を把握・分析してくれます
              </li>
              <li>
                <strong className="text-slate-900">Viewer で可視化</strong>：できた JSON を「← 戻る」からドロップ枠に放り込むと、テーブルやツリーで中身を確認できます
              </li>
            </ul>
          </section>
        </div>

        {/* URL 入力（このページの主役なので emphasized で目立たせる） */}
        <Section title="Step 1: URL を入力" alwaysOpen emphasized>
          <Field label="URL" desc={OPTION_DESCRIPTIONS.url}>
            <input
              type="url"
              value={state.url}
              onChange={(e) => setState({ ...state, url: e.target.value })}
              placeholder="https://example.com"
              className="w-full rounded-md border-2 border-slate-300 bg-white px-4 py-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </Field>
        </Section>

        {/* オプション群の見出し（任意なので muted で控えめに） */}
        <Section
          title="Step 2: オプション設定（任意）"
          subtitle="URL だけで実行できます。必要なときだけ下のセクションを開いてください"
          muted
        />

        <CollapsibleSection
          title="クロール対象"
          subtitle="どこまで・どれだけ巡回するか（深さ・上限・サブドメイン）"
          open={openSection.crawl}
          onToggle={() => toggleSection('crawl')}
        >
          <Field label="深さ (-d, --depth)" desc={OPTION_DESCRIPTIONS.depth}>
            <NumberInput
              value={state.depth}
              onChange={(v) => setState({ ...state, depth: v })}
              placeholder="既定: 5"
            />
          </Field>
          <Field
            label="最大ページ数 (--max-pages)"
            desc={OPTION_DESCRIPTIONS.maxPages}
          >
            <NumberInput
              value={state.maxPages}
              onChange={(v) => setState({ ...state, maxPages: v })}
              placeholder="既定: 5000"
            />
          </Field>
          <CheckboxField
            label="サブドメインを含める (--include-subdomain)"
            desc={OPTION_DESCRIPTIONS.includeSubdomain}
            checked={state.includeSubdomain}
            onChange={(v) => setState({ ...state, includeSubdomain: v })}
          />
        </CollapsibleSection>

        {/* オプション: 出力制御 */}
        <CollapsibleSection
          title="出力制御"
          subtitle="保存先や、JSON に含める情報の取捨選択（軽量化・上書き）"
          open={openSection.output}
          onToggle={() => toggleSection('output')}
        >
          <Field label="出力先 (-o, --output)" desc={OPTION_DESCRIPTIONS.output}>
            <input
              type="text"
              value={state.output}
              onChange={(e) => setState({ ...state, output: e.target.value })}
              placeholder="既定: ~/Desktop/sitemap-{host}-{timestamp}.json"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </Field>
          <CheckboxField
            label="既存ファイルを上書き (-f, --force)"
            desc={OPTION_DESCRIPTIONS.force}
            checked={state.force}
            onChange={(v) => setState({ ...state, force: v })}
          />
          <CheckboxField
            label="軽量出力 (--slim)"
            desc={OPTION_DESCRIPTIONS.slim}
            checked={state.slim}
            onChange={(v) =>
              setState({
                ...state,
                slim: v,
                // --slim を ON にすると個別フラグは無効化（ただし state は保持）
              })
            }
          />
          {!state.slim && (
            <>
              <CheckboxField
                label="内部リンク詳細を省略 (--no-internal-links)"
                desc={OPTION_DESCRIPTIONS.noInternalLinks}
                checked={state.noInternalLinks}
                onChange={(v) => setState({ ...state, noInternalLinks: v })}
              />
              <CheckboxField
                label="画像情報を省略 (--no-images)"
                desc={OPTION_DESCRIPTIONS.noImages}
                checked={state.noImages}
                onChange={(v) => setState({ ...state, noImages: v })}
              />
            </>
          )}
        </CollapsibleSection>

        {/* オプション: クロール挙動 */}
        <CollapsibleSection
          title="クロール挙動"
          subtitle="リクエスト速度・並列数・タイムアウト・User-Agent（サーバー負荷の調整）"
          open={openSection.behavior}
          onToggle={() => toggleSection('behavior')}
        >
          <Field
            label="並列数 (-c, --concurrency)"
            desc={OPTION_DESCRIPTIONS.concurrency}
          >
            <NumberInput
              value={state.concurrency}
              onChange={(v) => setState({ ...state, concurrency: v })}
              placeholder="既定: 3"
            />
          </Field>
          <Field
            label="レート制限 (--rate-limit)"
            desc={OPTION_DESCRIPTIONS.rateLimit}
          >
            <NumberInput
              value={state.rateLimit}
              onChange={(v) => setState({ ...state, rateLimit: v })}
              placeholder="既定: 5 (req/秒)"
            />
          </Field>
          <Field
            label="タイムアウト (--timeout)"
            desc={OPTION_DESCRIPTIONS.timeout}
          >
            <NumberInput
              value={state.timeout}
              onChange={(v) => setState({ ...state, timeout: v })}
              placeholder="既定: 10000 (ms)"
            />
          </Field>
          <Field
            label="User-Agent (--user-agent)"
            desc={OPTION_DESCRIPTIONS.userAgent}
          >
            <input
              type="text"
              value={state.userAgent}
              onChange={(e) => setState({ ...state, userAgent: e.target.value })}
              placeholder="既定: 内蔵 UA"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </Field>
        </CollapsibleSection>

        {/* オプション: JS 描画 */}
        <CollapsibleSection
          title="JS 描画"
          subtitle="SPA や JavaScript で内容を出すサイト向け（Playwright を起動するか）"
          open={openSection.js}
          onToggle={() => toggleSection('js')}
        >
          <Field label="モード" desc={OPTION_DESCRIPTIONS.js}>
            <div className="flex gap-3 text-sm">
              {(['auto', 'on', 'off'] as const).map((mode) => (
                <label
                  key={mode}
                  className="flex cursor-pointer items-center gap-1.5"
                >
                  <input
                    type="radio"
                    name="js-mode"
                    value={mode}
                    checked={state.js === mode}
                    onChange={() => setState({ ...state, js: mode })}
                  />
                  {mode === 'auto'
                    ? 'auto（自動判定）'
                    : mode === 'on'
                      ? '強制 ON (--js)'
                      : '強制 OFF (--no-js)'}
                </label>
              ))}
            </div>
          </Field>
        </CollapsibleSection>

        {/* オプション: 上級者向け */}
        <CollapsibleSection
          title="上級者向け"
          subtitle="通常は触らない設定（robots.txt の扱いなど）"
          open={openSection.advanced}
          onToggle={() => toggleSection('advanced')}
        >
          <CheckboxField
            label="robots.txt を無視 (--no-respect-robots)"
            desc={OPTION_DESCRIPTIONS.noRespectRobots}
            checked={state.noRespectRobots}
            onChange={(v) => setState({ ...state, noRespectRobots: v })}
          />
        </CollapsibleSection>

        {/* 生成されたコマンド */}
        <div
          className={`sticky bottom-4 overflow-hidden rounded-lg border bg-slate-900 shadow-lg transition-colors ${
            state.url ? 'border-emerald-500/60 ring-1 ring-emerald-500/40' : 'border-slate-300'
          }`}
        >
          <div className="flex items-center justify-between px-4 pb-2 pt-3">
            <span className="text-xs font-semibold uppercase tracking-wide">
              {state.url ? (
                <span className="text-emerald-400">✅ コマンドができました</span>
              ) : (
                <span className="text-slate-400">📋 生成されたコマンド</span>
              )}
            </span>
            <button
              onClick={handleCopy}
              disabled={!state.url}
              className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {copied ? '✅ コピーしました' : '📋 コピー'}
            </button>
          </div>
          {state.url && (
            <div className="bg-slate-800/60 px-4 py-2 text-[11px] text-slate-300">
              <span className="text-emerald-400">↓</span> 下のコマンドを{' '}
              <strong className="text-white">ターミナル（zsh / bash など）</strong> に貼り付けて{' '}
              <kbd className="rounded bg-slate-700 px-1.5 py-0.5 font-mono text-[10px] text-slate-100">Enter</kbd>{' '}
              で実行してください
            </div>
          )}
          <pre className="overflow-x-auto whitespace-pre-wrap break-all px-4 pb-3 pt-3 font-mono text-xs text-slate-100">
            {state.url
              ? command
              : '↑ URL を入力するとコマンドが表示されます'}
          </pre>
        </div>
      </main>
    </div>
  );
}

// ─── 補助コンポーネント ───

function Section({
  title,
  subtitle,
  children,
  alwaysOpen,
  emphasized,
  muted,
}: {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  alwaysOpen?: boolean;
  emphasized?: boolean;
  muted?: boolean;
}) {
  // muted: Step 2 のような「見出しだけ・控えめ」用。枠なしで pt 余白だけ取って区切る
  if (muted) {
    return (
      <div className="px-1 pt-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h2>
        {subtitle && <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>}
      </div>
    );
  }
  // emphasized: Step 1 のような「主役」用。青のリング + 太い見出し
  return (
    <div
      className={`rounded-lg border ${
        emphasized
          ? 'border-blue-300 bg-blue-50/40 p-5 ring-1 ring-blue-200'
          : 'border-slate-200 bg-white p-4'
      }`}
    >
      <h2
        className={
          emphasized
            ? 'text-base font-bold text-slate-900'
            : 'text-sm font-semibold text-slate-800'
        }
      >
        {title}
      </h2>
      {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
      {alwaysOpen && children && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

function CollapsibleSection({
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white/60">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-slate-50"
      >
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-700">{title}</div>
          {subtitle && (
            <div className="mt-0.5 text-xs font-normal text-slate-400">{subtitle}</div>
          )}
        </div>
        <span className="shrink-0 text-slate-400">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="space-y-3 border-t border-slate-100 bg-white p-4">{children}</div>}
    </div>
  );
}

function Field({
  label,
  desc,
  children,
}: {
  label: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
        {label}
        <HelpMark text={desc} />
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function CheckboxField({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded"
      />
      <span>{label}</span>
      <HelpMark text={desc} />
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
    />
  );
}

function HelpMark({ text }: { text: string }) {
  const [show, setShow] = useState(false);

  // 外側クリックで閉じる
  const wrapperRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!show) return;
    const onDoc = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setShow(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [show]);

  return (
    <span ref={wrapperRef} className="relative inline-flex">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={(e) => {
          // <label> 配下に置かれたとき、チェックボックスの toggle を巻き込まないようにする
          e.preventDefault();
          e.stopPropagation();
          setShow((v) => !v);
        }}
        aria-label="ヘルプ"
        className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 hover:bg-slate-300"
      >
        ?
      </button>
      {show && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-full z-50 mt-1.5 w-64 -translate-x-1/2 rounded-md border border-slate-300 bg-white p-2 text-[11px] font-normal leading-snug text-slate-700 shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
}

// ─── コマンド生成ロジック ───

function buildCommand(s: BuilderState): string {
  const parts: string[] = ['npx', '4ge-sitemap@latest'];

  // URL は必須
  if (s.url) parts.push(quote(s.url));

  // クロール対象
  if (s.depth) parts.push('-d', s.depth);
  if (s.maxPages) parts.push('--max-pages', s.maxPages);
  if (s.includeSubdomain) parts.push('--include-subdomain');

  // 出力制御
  if (s.output) parts.push('-o', quote(s.output));
  if (s.force) parts.push('-f');
  if (s.slim) {
    parts.push('--slim');
  } else {
    if (s.noInternalLinks) parts.push('--no-internal-links');
    if (s.noImages) parts.push('--no-images');
  }

  // クロール挙動
  if (s.concurrency) parts.push('-c', s.concurrency);
  if (s.rateLimit) parts.push('--rate-limit', s.rateLimit);
  if (s.timeout) parts.push('--timeout', s.timeout);
  if (s.userAgent) parts.push('--user-agent', quote(s.userAgent));

  // JS 描画
  if (s.js === 'on') parts.push('--js');
  if (s.js === 'off') parts.push('--no-js');

  // 上級者向け
  if (s.noRespectRobots) parts.push('--no-respect-robots');

  return parts.join(' ');
}

// 値にスペースや特殊文字が含まれてたらクオートで囲う
function quote(s: string): string {
  if (/[\s"'$`\\]/.test(s)) {
    return `'${s.replace(/'/g, "'\\''")}'`;
  }
  return s;
}
